import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useStore } from 'react-redux';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import type { RootState } from '../../store';
import { getPlatformUserId } from '../../../utils/utils';
import { getMostRecentByDate } from '../../../utils/dateUtils';
import {
    addMessage,
    setIsLoading,
    updateMessage,
    updateMessageMetadataById,
    removeMessages,
    setError,
    setReasoning,
    Message,
    initialiseMessageThread,
    setIsLoadingChatHistory,
    setGlobalSuggestedQuestions,
    setAskAicaRightPanelMode,
    ReasoningStep,
    resetReasoning,
    setIsClearingChat,
    setLibraryLoading,
    setLibraryFiles,
    addLibraryFiles,
    LibraryFile,
    AgentInput,
    setConversations,
    setLoadingConversations,
    resetChatView,
    setSelectedConversation,
    setPendingNewConversation,
    setSelectedConversationIsDeleted,
} from './actions';
import {
    clearMemory,
    getAskAicaFiles,
    getChatHistory,
    getOrgConversations,
    openReattachStream,
    stopAskAicaRun,
    streamAskAicaChat,
    GeneratedFileAttachment,
    ReattachFrame,
} from './apiHelper';
import {
    ASK_AICA_SOURCE_LEAD,
    CLARIFICATION_CANCEL_REASON,
    CLARIFICATION_REPLY_QUERY_SOURCE,
    CLARIFICATION_STALE_ERROR,
    DEFAULT_SUGGESTED_QUESTIONS,
    FLOW_KEY_FEEDBACK,
    isInProgressStatus,
    MESSAGE_STATUS,
    STOP_SETTLE_FALLBACK_MS,
} from './constants';
import { CHAT_CONFIG } from './service';
import { deriveNewestTurn, findClarificationMessage, getPendingClarification, hasPendingClarification, selectViewOnly } from './selectors';
import {
    applyClarificationResolution,
    applyOptimisticClarificationAnswers,
    buildClarificationAnswerSummary,
    settleClarificationAsExpired,
} from './clarificationRound';
import { ClarificationAnswer, ClarificationCard, ClarificationResolution, ClarificationResponse } from './clarificationTypes';

// Maps a just-generated file's attachment metadata (already present in the chat
// completion response) straight into a LibraryFile — avoids a redundant
// getAskAicaFiles round-trip on every generation. createdAt isn't part of this
// payload (only the Library listing endpoint has it), so we use "now"; the next
// real fetch (e.g. panel reopen) overwrites it with the authoritative value.
const mapAttachmentToLibraryFile = (attachment: GeneratedFileAttachment): LibraryFile => ({
    fileUrl: attachment.file_url,
    fileName: attachment.file_name,
    fileType: attachment.file_type,
    createdAt: new Date().toISOString(),
    source: attachment.source || '',
    mnemosyneDocId: attachment.mnemosyne_doc_id || '',
});

export const useAskAica = () => {
    const streamStartedCallbackRef = useRef<((value: boolean) => void) | null>(null);
    // Persistent (not a sendMessage-local closure) so deleteConversation's ACTIVE
    // branch can abort an in-flight stream too (SF-4 parity with
    // useGlobalAskAica.ts's abortStreamRef) — otherwise a late chunk from the
    // just-deleted conversation can re-assert its conversation_id after deletion.
    const abortStreamRef = useRef<(() => void) | null>(null);
    // Handle on a live re-attach stream (RECUR-28136 CodeRabbit finding 1). Without
    // this, newChat/deleteConversation/a conversation switch leave the old run's
    // stream open — it keeps reconnecting until [DONE]/'fatal'/REATTACH_MAX_WALL_MS,
    // and every frame it processes dispatches setReasoning, which the leads reducer
    // writes to a single global slot regardless of messageId, bleeding one
    // conversation's reasoning into whichever one is on screen.
    const closeReattachRef = useRef<(() => void) | null>(null);
    const closeReattach = useCallback(() => {
        closeReattachRef.current?.();
        closeReattachRef.current = null;
    }, []);
    const dispatch = useAppDispatch();
    // useStore() resolves the store from Provider context at render time, unlike the
    // old module-level import (which pulled in every root reducer and broke tests
    // importing this hook). Live read still required for stopRun below.
    const store = useStore<RootState>();
    const user_id = useAppSelector((state) => state.user.userData.userId);
    const user_name = useAppSelector((state) => state.user.userData.userName || undefined);
    const params = useParams();
    const investorOrgId = useAppSelector((state) => state.user.userData.investorOrgId);
    const {
        messages,
        reasoning,
        isLoading,
        error,
        uiState,
        pagination,
        isLoadingChatHistory,
        selectedConversationId,
        pendingNewConversation,
        conversations,
    } = useAppSelector((state) => state.dashboard.askAicaReducer);
    const isViewOnly = useAppSelector(selectViewOnly);
    const { selectedSources } = uiState;
    const org_id = params.id ? params.id : investorOrgId;
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // D4(β)/SF-1: always-live snapshot of the values fetchChatHistory's open-time
    // resolution must re-check once its async list fetch resolves — mirrors
    // useGlobalAskAica.ts's liveStateRef (F-13). Never read the closure values
    // directly inside that async branch; they may be stale by the time it runs.
    const liveStateRef = useRef({ isLoading, messagesLen: messages.length, selectedConversationId });
    useLayoutEffect(() => {
        liveStateRef.current = { isLoading, messagesLen: messages.length, selectedConversationId };
    }, [isLoading, messages.length, selectedConversationId]);

    // True once reconnecting to a still-running turn has hit REATTACH_MAX_WALL_MS.
    // NOT a failure state: the working state and Stop both stay, only the copy changes.
    const [reattachExhausted, setReattachExhausted] = useState(false);

    /**
     * The degraded fallback (D16): ONE history read, merged BY ID into the thread
     * already on screen. Reusing fetchChatHistory(…, reset=true) here would be
     * wrong — its reducer APPENDS the page and ADVANCES the pager, so it would
     * duplicate page 1 and silently skip a page on the next load-more. Patching the
     * rows in place leaves scrollback, ordering and pagination untouched.
     */
    const mergeHistoryIntoThread = useCallback(
        (conversationId?: string) => {
            getChatHistory(user_id, org_id, 1, pagination.pageSize, conversationId)
                .then((data) => {
                    // This is exactly the slow response most likely to land after the
                    // user has switched conversations — discard it if they have.
                    if (conversationId && conversationId !== liveStateRef.current.selectedConversationId) return;
                    const items = Array.isArray(data?.items) ? data.items : [];
                    items.forEach((item: { assistant_message?: Message }) => {
                        const assistant = item.assistant_message;
                        if (!assistant) return;
                        dispatch(
                            updateMessage(assistant.id, (assistant.input_text as string) ?? '', assistant.metadata ?? {}, assistant.status),
                        );
                    });
                })
                .catch((err) => {
                    // Never log the raw error — an AxiosError's `config.headers` carries the
                    // session cerberus token (see useAskAicaFileActions.ts::openFile).
                    console.error('Error refetching chat history:', err instanceof Error ? err.message : 'unknown error');
                });
        },
        [dispatch, user_id, org_id, pagination.pageSize],
    );

    /** Rewrites a round's card in place — the card lives on an OLDER message than the
     * turn currently streaming, so it is addressed by tool_use_id, off the live store. */
    const patchClarificationCard = useCallback(
        (toolUseId: string, transform: (card: ClarificationCard) => ClarificationCard) => {
            const message = findClarificationMessage(store.getState().dashboard.askAicaReducer.messages, toolUseId);
            const card = message?.metadata?.clarification;
            if (!message || !card) return;
            const next = transform(card);
            if (next !== card) dispatch(updateMessageMetadataById(message.id, { clarification: next }));
        },
        [dispatch, store],
    );

    /** Follow a turn that is still running server-side — the reloaded-page case.
     * Frames land through the SAME actions the live stream uses, keyed on the
     * server UUID the history mapper stamps as the message id. */
    const startReattach = useCallback(
        (userMessageId: string, assistantMessageId: number | string, conversationId?: string) => {
            const close = openReattachStream(userMessageId, {
                headers: CHAT_CONFIG.getHeaders(org_id, user_id),
                onFrame: (frame: ReattachFrame) => {
                    // The buffer replay stamps the settled terminal status onto its final
                    // frame's metadata — prefer that over the is_final hardcode so a
                    // stopped turn re-attaches as 'partial', not 'complete' (RECUR-28136).
                    const status = frame.is_final
                        ? (frame.metadata?.status as string | undefined) ?? MESSAGE_STATUS.COMPLETE
                        : MESSAGE_STATUS.RUNNING;
                    dispatch(updateMessage(assistantMessageId, frame.message?.content ?? '', frame.metadata ?? {}, status));
                    const resolution: ClarificationResolution | undefined = frame.metadata?.clarification_resolution;
                    if (resolution) {
                        patchClarificationCard(resolution.tool_use_id, (card) => applyClarificationResolution(card, resolution));
                    }
                    // Defense in depth (CodeRabbit finding 1): the reducer's setReasoning
                    // writes to a single global slot regardless of messageId, so a frame
                    // for a conversation no longer on screen would bleed its reasoning into
                    // whichever conversation the user has since switched to. Closing the
                    // stream on a switch (see fetchChatHistory/newChat/deleteConversation) is
                    // the primary fix; this is the belt-and-braces backstop.
                    if (
                        frame.reasoning &&
                        (conversationId === undefined || conversationId === liveStateRef.current.selectedConversationId)
                    ) {
                        dispatch(setReasoning(assistantMessageId, frame.reasoning, !!frame.has_new_reasoning));
                    }
                },
                onDegraded: () => mergeHistoryIntoThread(conversationId),
                onEnd: (outcome) => {
                    closeReattachRef.current = null;
                    if (outcome === 'fatal') {
                        // 403/404/422 — there is nothing to follow, so drop the working
                        // state rather than leave the bubble spinning forever.
                        dispatch(updateMessage(assistantMessageId, '', {}, MESSAGE_STATUS.COMPLETE));
                        return;
                    }
                    if (outcome === 'exhausted') {
                        // The second and last history read — the run may well have
                        // finished while the buffer stayed unreachable (D16).
                        mergeHistoryIntoThread(conversationId);
                        setReattachExhausted(true);
                    }
                },
            });
            if (close) {
                closeReattachRef.current = close;
                setReattachExhausted(false);
            }
        },
        [dispatch, org_id, user_id, mergeHistoryIntoThread, patchClarificationCard],
    );

    const fetchChatHistory = useCallback(
        // conversationId?: string | null
        //   undefined  → use selectedConversationId from the store (load-more falls through here);
        //                on the bare (isInitialFetch=false, reset=false) call shape this is also
        //                the "surface just opened" signal — see the D4(β) branch below.
        //   null       → force no conversation id (back-to-own escape hatch — see ConversationSidebar)
        //   string     → explicit id (row-click passes the id directly)
        (isInitialFetch = false, conversationId?: string | null, reset = false) => {
            // D4(β): on a bare call (no args at all — both AskAicaV2.tsx's
            // handleOpenModal and mobile's useMobileAskAicaLogic call
            // fetchChatHistory() this way on open), resolve and load the
            // caller's own most-recent conversation explicitly instead of
            // leaning on the server's active-conversation fallback — so the FE
            // holds an id, and the sidebar highlights a row, from the start.
            // Placed here (not in AskAicaV2.tsx) so mobile inherits it too.
            if (conversationId === undefined && !isInitialFetch && !reset) {
                const live = liveStateRef.current;
                // SF-1: never run — and never resetChatView — once a turn has
                // started, messages exist, or a conversation is already
                // selected. This must not collide with leads' auto-send-on-open
                // (AskAicaV2.tsx fires sendMessage ~100ms after mount): because
                // initMessageThread APPENDS, a late history page landing after
                // that turn started would interleave with it.
                if (live.isLoading || live.messagesLen > 0 || live.selectedConversationId) return;
                dispatch(setLoadingConversations(true));
                getOrgConversations(user_id, org_id, {
                    onSuccess: (data) => {
                        const list = data?.conversations ?? [];
                        dispatch(setConversations(list));
                        // Re-check live state — this fetch is async and a turn/selection
                        // may have started while it was in flight.
                        const liveNow = liveStateRef.current;
                        if (liveNow.isLoading || liveNow.messagesLen > 0 || liveNow.selectedConversationId) return;
                        // Explicit greatest-last_message_at pick rather than the list's
                        // first own row — the FE must not silently depend on the server's
                        // documented sort order (GET /chat/conversations is `last_message_at
                        // DESC NULLS LAST`) staying that way forever.
                        const ownConversations = list.filter((c: { user_id: string }) => c.user_id === getPlatformUserId());
                        const own = getMostRecentByDate(ownConversations, 'last_message_at');
                        if (own) {
                            dispatch(setSelectedConversation(own.conversation_id));
                            fetchChatHistory(false, own.conversation_id, true);
                        } else {
                            // MAN-026/A-33: a zero-own-conversations org has no explicit
                            // id to load, so the normal getChatHistory().then()/.catch()
                            // path below — the only other place that clears
                            // isLoadingChatHistory — never runs. Without this, the flag
                            // stays stuck at its `true` initial value forever, and
                            // AskAicaInputBox's `messages.length === 0 && isLoadingChatHistory`
                            // guard (:188) renders a blank pane (no greeting, no composer)
                            // until the user manually clicks New Chat. Land in the same
                            // empty state New Chat produces — messages are already []
                            // here (the SF-1 guard above), so this only needs to clear the
                            // stuck flag; NOT arming pendingNewConversation is deliberate —
                            // an id-less send on a zero-conversation org already
                            // force-creates server-side (no prior conversation to resume).
                            dispatch(resetChatView());
                        }
                    },
                    onError: (err) => {
                        console.error('Error fetching org conversations:', err instanceof Error ? err.message : 'unknown error');
                        dispatch(setLoadingConversations(false));
                    },
                });
                return;
            }

            const effectiveConversationId = conversationId === null ? undefined : conversationId ?? selectedConversationId ?? undefined;
            // A genuine switch to a DIFFERENT conversation (not a same-conversation
            // load-more) — stop following whatever run this tab was re-attached to
            // before loading the new thread (CodeRabbit finding 1).
            if (effectiveConversationId !== selectedConversationId) closeReattach();
            // On a conversation switch (reset=true), always fetch page 1 so we
            // don't close over a stale pagination.page from the previous chat.
            const page = reset ? 1 : pagination.page;
            dispatch(setIsLoadingChatHistory(true));
            getChatHistory(user_id, org_id, page, pagination.pageSize, effectiveConversationId)
                .then((data) => {
                    // Athena's share-view contract (GET .../history?conversation_id=...)
                    // echoes is_deleted for the conversation actually served — present
                    // whenever an explicit conversation_id was requested (own history,
                    // a sidebar row, or a cross-chat citation's navigation target), even
                    // when false. Absent on the bare own-default fetch, which has no
                    // single conversation to report on — skip the dispatch entirely
                    // there rather than reporting a deletion state for no conversation.
                    // effectiveConversationId correlates this response with the request
                    // that produced it (two fetchChatHistory calls for different
                    // conversations can overlap in flight) — the reducer discards a late
                    // response for a conversation the user has since navigated away from.
                    if (effectiveConversationId) {
                        dispatch(setSelectedConversationIsDeleted(effectiveConversationId, !!data?.is_deleted));
                    }

                    let messageThread: Message[] = [];
                    if (data && Array.isArray(data.items)) {
                        data.items.reverse().forEach((item: { user_message?: Message; assistant_message?: Message }) => {
                            if (item.assistant_message) {
                                const isFeedbackAck = !!(item.assistant_message.metadata as any)?.feedback_ack;
                                messageThread.push({
                                    id: item.assistant_message.id,
                                    type: item.assistant_message.type,
                                    timestamp: new Date(item.assistant_message.created_at as string),
                                    content: item.assistant_message.input_text as string,
                                    status: item.assistant_message.status as string,
                                    // Generated-file metadata (one-pager DOCX) — lets the
                                    // FileAttachmentCard render from history after refresh
                                    metadata: item.assistant_message.metadata,
                                    isFeedbackAck,
                                });
                            }
                            if (item.user_message) {
                                const content = item.user_message.input_text as string;
                                const isFeedback = item.user_message.query_source === 'feedback';
                                // A pill turn's flowKey/agentInput are FE-side context the history
                                // API never returns — re-derive them from the pill list by exact
                                // text, or a retry after a reload silently downgrades a scoped
                                // request to a plain chat one.
                                const pill = DEFAULT_SUGGESTED_QUESTIONS.find((question) => question.text === content);
                                messageThread.push({
                                    id: item.user_message.id,
                                    type: item.user_message.type,
                                    timestamp: new Date(item.user_message.created_at as string),
                                    content,
                                    status: item.user_message.status as string,
                                    // Map query_source='feedback' to flowKey so MessagesContainer
                                    // renders the distinct feedback bubble on reload (A-17).
                                    flowKey: isFeedback ? FLOW_KEY_FEEDBACK : pill?.flowKey,
                                    agentInput: pill?.agentInput,
                                    isClarificationAnswer: item.user_message.query_source === CLARIFICATION_REPLY_QUERY_SOURCE,
                                });
                            }
                        });

                        // If there are suggestions from chat history, set them as global suggestions
                        if (data.suggestions && Array.isArray(data.suggestions) && data.suggestions.length > 0) {
                            dispatch(setGlobalSuggestedQuestions(data.suggestions, effectiveConversationId));
                        }

                        dispatch(initialiseMessageThread(messageThread, effectiveConversationId));

                        // A turn still running server-side — only ever the newest one,
                        // which only page 1 carries. `!isLoading` is the no-double-stream
                        // guard: a tab that still owns the live XHR (panel closed, or
                        // navigated away and back) must never open a second stream for
                        // the same run. The cross-instance half of that guard is the
                        // module-level registry inside openReattachStream.
                        const newestTurn = deriveNewestTurn(messageThread[0], messageThread[1]);
                        if (
                            page === 1 &&
                            newestTurn.isInProgress &&
                            newestTurn.userMessageId &&
                            newestTurn.assistantMessageId !== undefined &&
                            !liveStateRef.current.isLoading
                        ) {
                            startReattach(newestTurn.userMessageId, newestTurn.assistantMessageId, effectiveConversationId);
                        }
                    }
                })
                .catch((err) => {
                    // Never log the raw error — an AxiosError's `config.headers` carries the
                    // session cerberus token (see useAskAicaFileActions.ts::openFile).
                    console.error('Error fetching chat history:', err instanceof Error ? err.message : 'unknown error');
                    toast.error('Failed to fetch chat history');
                    dispatch(setIsLoadingChatHistory(false));
                });
        },
        [user_id, org_id, pagination, selectedConversationId, startReattach, closeReattach],
    );

    // Fetch the Library file list for an org (Polus BEFE), via getAskAicaFiles's
    // onSuccess/onError handlers rather than a .then()/.catch() chain. Dispatching
    // setLibraryFiles on both paths (mapped files, or [] on error) also resets
    // isLoading in one place — including when onSuccess's own mapping throws,
    // since getAskAicaFiles routes that to onError too.
    const fetchLibraryFiles = useCallback(
        (orgId: string) => {
            dispatch(setLibraryLoading(true));
            getAskAicaFiles(orgId, {
                onSuccess: (files: any[]) => {
                    const libraryFiles: LibraryFile[] = files.map((file) => ({
                        fileUrl: file.file_url,
                        fileName: file.file_name,
                        fileType: file.file_type,
                        createdAt: file.created_at,
                        source: file.source,
                        mnemosyneDocId: file.mnemosyne_doc_id,
                    }));
                    dispatch(setLibraryFiles(libraryFiles));
                },
                onError: () => {
                    dispatch(setLibraryFiles([]));
                },
            });
        },
        [dispatch],
    );

    const fetchOrgConversations = useCallback(() => {
        if (!user_id || !org_id) return;
        // Capture the orgId at request time. If the user switches company before
        // the response arrives, skip the dispatch to prevent cross-tenant leakage.
        const requestedOrgId = org_id;
        dispatch(setLoadingConversations(true));
        getOrgConversations(user_id, org_id, {
            onSuccess: (data) => {
                if (requestedOrgId !== org_id) return;
                dispatch(setConversations(data?.conversations ?? []));
            },
            onError: (err) => {
                // Never log the raw error — an AxiosError's `config.headers` carries the
                // session cerberus token (see useAskAicaFileActions.ts::openFile).
                console.error('Error fetching org conversations:', err instanceof Error ? err.message : 'unknown error');
                dispatch(setLoadingConversations(false));
            },
        });
    }, [user_id, org_id, dispatch]);

    // Function to send a message and get a streaming response. `clarificationResponse`
    // makes the turn an answer to a pending round (the server resumes the paused turn).
    const sendTurn = useCallback(
        (content: string, flowKey?: string, agentInput?: AgentInput, clarificationResponse?: ClarificationResponse) => {
            if (!content.trim() || isLoading) return;
            // A paused round blocks every non-answer send; the server's new_turn cancel is only the backstop.
            if (!clarificationResponse && hasPendingClarification(messages[0])) return;

            // The server answered "already answered / no such round": undo the optimistic
            // pair and expire the card, unless the live resolution already settled it.
            const rollBackStaleAnswer = (optimisticIds: Array<number | string>) => {
                dispatch(removeMessages(optimisticIds));
                if (!clarificationResponse) return;
                patchClarificationCard(clarificationResponse.tool_use_id, (card) =>
                    settleClarificationAsExpired(card, CLARIFICATION_CANCEL_REASON.NEW_TURN),
                );
            };

            // The user's own picks are known now: show the Q&A note immediately instead of waiting on the server.
            const settleRoundOptimistically = () => {
                if (!clarificationResponse) return;
                patchClarificationCard(clarificationResponse.tool_use_id, (card) =>
                    applyOptimisticClarificationAnswers(card, clarificationResponse.answers),
                );
            };
            settleRoundOptimistically();

            // Reset stream started state when sending a new message
            if (streamStartedCallbackRef.current) {
                streamStartedCallbackRef.current(false);
            }

            // Create and add user message
            const userMessage = {
                id: Date.now(),
                type: 'user' as const,
                content,
                timestamp: new Date(),
                sources: selectedSources,
                flowKey,
                agentInput,
                isClarificationAnswer: !!clarificationResponse,
            };
            dispatch(addMessage(userMessage));

            // Create placeholder for AI response
            const aiMessageId = Date.now() + 1;
            const aiMessage = {
                id: aiMessageId,
                type: 'assistant' as const,
                content: '',
                timestamp: new Date(),
            };
            dispatch(addMessage(aiMessage));
            dispatch(setIsLoading(true));
            dispatch(resetReasoning());
            dispatch(setAskAicaRightPanelMode('reasoning'));

            // Format messages for API. Belt-and-braces (live-probe fix, dev repro:
            // view team conv → back to own → New Chat → send → New Chat → send):
            // whenever a New Chat is pending, send ONLY the just-typed message —
            // NEVER whatever `messages` currently holds. This makes the previous
            // conversation's last-3 window structurally unreachable regardless of
            // any race that might leave `messages` unreset by the time this runs
            // (observed on dev: pendingNewConversation armed correctly, but the
            // wire body still leaked the prior conversation's last exchange).
            // Harmless in the already-correct case too, since messages is already
            // [] whenever pendingNewConversation is true.
            const apiMessages = pendingNewConversation
                ? [{ role: 'user' as const, content: userMessage.content }]
                : [...messages]
                      .reverse()
                      .concat(userMessage)
                      .slice(-3)
                      .map((msg) => ({
                          role: msg.type === 'user' ? 'user' : 'assistant',
                          content: msg.content,
                      }));

            // SF-2: attach the held conversation_id only for an OWN selection —
            // never a teammate's. handleRowClick is shared by both sidebar
            // sections (ConversationSidebar.tsx), so selecting a teammate's row
            // sets the same selectedConversationId; the composer is hidden for
            // view-only rows, but the auto-send-on-open path bypasses it
            // (LeftPanel wires onSendMessageRef regardless of isViewOnly), and
            // sending a teammate's conversation_id would 403.
            const conversationIdToSend = !isViewOnly && selectedConversationId ? selectedConversationId : undefined;
            // D1/D2: never send the flag together with an explicit id.
            const newConversationFlag = pendingNewConversation && !conversationIdToSend ? true : undefined;

            try {
                // Stream the response
                abortStreamRef.current = streamAskAicaChat(
                    apiMessages,
                    selectedSources,
                    user_id,
                    org_id,
                    // On each chunk
                    (chunk, suggestedQuestions, reasoning, hasNewReasoning, isFirst, metadata, chunkConversationId) => {
                        // Update the message content
                        dispatch(updateMessage(aiMessageId, chunk, metadata || {}));

                        // The resume's final frame settles the card on the OLDER paused message.
                        const resolution: ClarificationResolution | undefined = metadata?.clarification_resolution;
                        if (resolution) {
                            patchClarificationCard(resolution.tool_use_id, (card) => applyClarificationResolution(card, resolution));
                        }

                        // If this is actual content (not just a step_type update), notify that streaming has started
                        if (chunk && streamStartedCallbackRef.current) {
                            streamStartedCallbackRef.current(true);
                        }

                        // Handle suggested questions if present
                        if (suggestedQuestions && suggestedQuestions.length > 0) {
                            dispatch(setGlobalSuggestedQuestions(suggestedQuestions));
                        }

                        // Handle reasoning if present
                        dispatch(setReasoning(aiMessageId, reasoning as ReasoningStep[], hasNewReasoning));

                        // D2/D5: the first chunk carrying a conversation_id becomes the
                        // composer target — also clears pendingNewConversation
                        // (reducer.ts::setSelectedConversation).
                        if (chunkConversationId && chunkConversationId !== selectedConversationId) {
                            dispatch(setSelectedConversation(chunkConversationId));
                        }
                    },
                    // On complete
                    (response) => {
                        dispatch(setIsLoading(false));
                        if (response.conversationId && response.conversationId !== selectedConversationId) {
                            dispatch(setSelectedConversation(response.conversationId));
                        }
                        if (response.metadata?.clarification_stale) {
                            rollBackStaleAnswer([userMessage.id, aiMessageId]);
                            return;
                        }
                        if (response.metadata) {
                            // The live final frame now carries its settled status too
                            // (Athena's _finalize_stream / _run_producer cancellation
                            // path) — without passing it here, a live Stop (no reload)
                            // never showed "Generation stopped" (RECUR-28136).
                            dispatch(updateMessage(aiMessageId, '', response.metadata, response.metadata?.status));
                        }

                        // Refresh the sidebar so a new conversation title appears
                        // in "Your Conversations" without waiting for the next poll.
                        fetchOrgConversations();

                        // A completed message that generated a file (e.g. a one-pager) already
                        // carries the full per-file detail needed for the Library list — add it
                        // directly instead of refetching. Only files that were durably persisted
                        // (mnemosyne_doc_id set) are added, since only those ever appear in a
                        // real fetch — an ephemeral/upload-failed file would never match on refetch.
                        const attachments = response.metadata?.attachments;
                        if (attachments?.length) {
                            const durableFiles = attachments
                                .filter((attachment) => !!attachment.mnemosyne_doc_id)
                                .map(mapAttachmentToLibraryFile);
                            if (durableFiles.length > 0) dispatch(addLibraryFiles(durableFiles));
                        } else if (response.metadata?.file_url) {
                            // Defensive fallback for a metadata shape without per-file detail —
                            // refetch so the Library list still reflects the new file.
                            fetchLibraryFiles(org_id);
                        }

                        // Set final suggested questions if any
                        if (response.suggestedQuestions && response.suggestedQuestions.length > 0) {
                            dispatch(setGlobalSuggestedQuestions(response.suggestedQuestions));
                        }

                        // Set final reasoning if any
                        dispatch(setReasoning(aiMessageId, response.reasoning as ReasoningStep[], response.hasNewReasoning));

                        // Set right panel mode to suggestions after 2 seconds
                        setTimeout(() => {
                            dispatch(setAskAicaRightPanelMode('suggestions'));
                        }, 2000);
                    },
                    // On error
                    (error) => {
                        console.error('Error in streamAskAicaChat:', error instanceof Error ? error.message : 'unknown error');
                        dispatch(setIsLoading(false));
                        if (error?.code === CLARIFICATION_STALE_ERROR) {
                            rollBackStaleAnswer([userMessage.id, aiMessageId]);
                            return;
                        }
                        dispatch(setError(error?.message || 'Failed to get response'));

                        // R-3: the New Chat flag was set but the stream failed before its
                        // first chunk — no id was ever captured. The conversation may
                        // already exist server-side (D1 creates before the LLM runs), so
                        // clear the flag and refetch: a retry resumes it rather than
                        // force-creating a second one.
                        if (newConversationFlag && !selectedConversationId) {
                            dispatch(setPendingNewConversation(false));
                            fetchOrgConversations();
                        }

                        // A-29: a held conversation_id was deleted elsewhere — drop it,
                        // refresh the sidebar, and land in a fresh New Chat instead of
                        // surfacing the raw "HTTP error! Status: 404".
                        if (error?.status === 404) {
                            dispatch(resetChatView());
                            dispatch(setSelectedConversation(null));
                            dispatch(setPendingNewConversation(true));
                            dispatch(setError('This conversation is no longer available. Starting a new chat.'));
                            fetchOrgConversations();
                        }
                    },
                    flowKey,
                    agentInput,
                    user_name,
                    conversationIdToSend,
                    ASK_AICA_SOURCE_LEAD,
                    newConversationFlag,
                    clarificationResponse,
                );
            } catch (error: any) {
                dispatch(setIsLoading(false));
                dispatch(setError(error.message || 'Failed to send message'));
            }

            // Return cleanup function
            return () => {
                abortStreamRef.current?.();
            };
        },
        [
            dispatch,
            isLoading,
            messages,
            selectedSources,
            fetchLibraryFiles,
            fetchOrgConversations,
            user_id,
            org_id,
            user_name,
            selectedConversationId,
            pendingNewConversation,
            isViewOnly,
            patchClarificationCard,
        ],
    );

    const sendMessage = useCallback(
        (content: string, flowKey?: string, agentInput?: AgentInput) => sendTurn(content, flowKey, agentInput),
        [sendTurn],
    );

    /** Answers the pending round: ONE send carrying every question's pick/typed text. */
    const answerClarification = useCallback(
        (card: ClarificationCard, answers: ClarificationAnswer[]) =>
            sendTurn(buildClarificationAnswerSummary(card, answers), undefined, undefined, { tool_use_id: card.tool_use_id, answers }),
        [sendTurn],
    );

    // D3 per-row delete: branches on whether the deleted id is the composer
    // target. ACTIVE (id === selectedConversationId) resets the view and
    // arms New Chat, mirroring the old clearChatMemory's optimistic reset.
    // BACKGROUND (any other row) leaves messages/selectedConversationId/view
    // completely untouched — only the sidebar list changes.
    const deleteConversation = useCallback(
        (targetConversationId: string) => {
            const isActive = targetConversationId === selectedConversationId;
            if (isActive) {
                // SF-4: abort any in-flight stream BEFORE resetting the view — otherwise
                // a late chunk from the just-deleted conversation can re-assert its
                // conversation_id after deletion (mirrors useGlobalAskAica.ts).
                abortStreamRef.current?.();
                abortStreamRef.current = null;
                closeReattach();
                // Optimistic: empty the center immediately so the user sees a clean slate.
                dispatch(resetChatView());
            }
            dispatch(setIsClearingChat(true));
            clearMemory(user_id, org_id, targetConversationId)
                .then(() => {
                    // Per-row delete — a background delete never clears the active
                    // chat, so "chat memory cleared" overstates what happened.
                    toast.success('Conversation deleted');
                    dispatch(setIsClearingChat(false));
                    // Optimistic row removal — the DB soft-deletes are already
                    // synchronous by the time this 200 lands, so the follow-up
                    // fetchOrgConversations() below is a correctness refresh, not
                    // the source of truth for removing the row.
                    dispatch(setConversations(conversations.filter((c) => c.conversation_id !== targetConversationId)));
                    if (isActive) {
                        dispatch(setSelectedConversation(null));
                        dispatch(setPendingNewConversation(true));
                    }
                    fetchOrgConversations();
                })
                .catch((err) => {
                    console.error('Error clearing chat:', err instanceof Error ? err.message : 'unknown error');
                    dispatch(setIsClearingChat(false));
                    toast.error('Failed to delete conversation');
                    // Resync sidebar in case the clear partially succeeded.
                    fetchOrgConversations();
                });
        },
        [dispatch, user_id, org_id, selectedConversationId, conversations, fetchOrgConversations, closeReattach],
    );

    // Header "Delete Chat" — targets selectedConversationId ONLY; no-op (and the
    // header button is disabled, see LeftPanel.tsx) when there is no explicit
    // conversation target (D4). Signature unchanged (no args) so mobile — which
    // calls this the same way — is unaffected.
    const clearChatMemory = useCallback(() => {
        if (!selectedConversationId) return;
        deleteConversation(selectedConversationId);
    }, [selectedConversationId, deleteConversation]);

    // New Chat — resets the view and arms the next send to force-create a
    // conversation (D1/D2); never creates a row itself (no ghost rows).
    // Mixpanel tracking lives at the sidebar click-handler layer (mirrors
    // portfolio's PortfolioConversationSidebar) — NOT here, so a click fires
    // exactly one AICA_ASK_AICA_NEW_CHAT_CLICKED (A-39), not two.
    // SF-4 parity (live-probe fix): abort any in-flight stream from the
    // abandoned conversation FIRST — mirrors useGlobalAskAica.ts's newChat.
    // Without this, a stream still running when New Chat is clicked keeps
    // executing and its onComplete/onChunk can still dispatch against the
    // just-reset view.
    const newChat = useCallback(() => {
        abortStreamRef.current?.();
        abortStreamRef.current = null;
        closeReattach();
        dispatch(resetChatView());
        dispatch(setSelectedConversation(null));
        dispatch(setPendingNewConversation(true));
    }, [dispatch, closeReattach]);

    const registerStreamStartedCallback = (callback: (value: boolean) => void) => {
        streamStartedCallbackRef.current = callback;
    };

    // Leads messages are NEWEST-FIRST — addMessage prepends, the history mapper
    // reverses items, and the reducer appends older pages onto the tail — so the
    // newest turn is [0] (assistant) and [1] (its user message).
    const newestTurn = deriveNewestTurn(messages[0], messages[1]);
    const pendingClarification = getPendingClarification(messages[0]);

    /** Stop the run server-side. A client disconnect is no longer a cancel, and
     * the local abort handle is gone entirely after a reload — this endpoint is
     * what makes Stop work in both cases. Idempotent, so a second press is safe.
     *
     * Resolves true once the server has accepted the stop — the caller must then
     * leave the stream OPEN rather than aborting it, so Athena's cancellation
     * teardown can still push its synthetic final frame down it (that frame is
     * what settles message.status and lets "Generation stopped" render). Resolves
     * false when the request itself failed (already logged in stopAskAicaRun),
     * in which case the run may never have stopped and the caller should fall
     * back to the old local-abort behaviour.
     *
     * Also arms a bounded local fallback (STOP_SETTLE_FALLBACK_MS): if that final
     * frame never arrives, the turn settles locally instead of leaving the
     * composer blocked forever. Reads straight off the Redux store (not
     * liveStateRef) so a real settlement landing AFTER this hook has unmounted
     * (Stop, then navigate away) is never stomped: liveStateRef's sync effect
     * stops firing on unmount and would otherwise freeze at the value captured
     * the instant Stop fired. Gates on the assistant row's OWN status, not
     * `isLoading` — a turn re-attached after a reload never sets `isLoading`
     * true (that flag only tracks a send THIS TAB started), so `isLoading`
     * alone would never fire the fallback for it and the composer would stay
     * blocked until the panel is reopened (CodeRabbit finding 2). `isLoading`
     * is ALSO not safe as a sufficient condition: a newer turn started in the
     * same conversation after this one already settled also leaves `isLoading`
     * true, so the abort/setIsLoading(false) side effects additionally require
     * this captured turn to still be the thread's active one (CodeRabbit
     * follow-up finding). */
    const stopRun = useCallback(() => {
        if (!newestTurn.userMessageId) return Promise.resolve(false);
        const assistantMessageId = newestTurn.assistantMessageId;
        return stopAskAicaRun(newestTurn.userMessageId, CHAT_CONFIG.getHeaders(org_id, user_id))
            .then((stopResult) => {
                // Stopping a paused round cancels it server-side (no stream, so no final frame to
                // settle it). `already_terminal` covers a second tab / a retried Stop whose first 200
                // was lost — either way the round is over, so settle the card and free the composer.
                if (pendingClarification && stopResult?.clarification_cancelled) {
                    patchClarificationCard(pendingClarification.tool_use_id, (card) =>
                        settleClarificationAsExpired(card, CLARIFICATION_CANCEL_REASON.STOP),
                    );
                } else if (pendingClarification && stopResult?.already_terminal) {
                    // The round may have been answered elsewhere — re-sync rather than stamp it expired.
                    mergeHistoryIntoThread(selectedConversationId ?? undefined);
                }
                if (assistantMessageId !== undefined) {
                    setTimeout(() => {
                        const state = store.getState().dashboard.askAicaReducer;
                        const row = state.messages.find((message: Message) => message.id === assistantMessageId);
                        // The row's OWN status is the sole settle gate — `isLoading` can't
                        // tell this turn apart from a NEWER one started in the same
                        // conversation after this one already settled (that newer turn
                        // would also leave isLoading true).
                        if (!isInProgressStatus(row?.status)) return; // already settled
                        // Only abort the live stream / clear isLoading if this captured
                        // turn is still the thread's active one — otherwise a newer turn's
                        // stream would be killed by this stale timer.
                        const isStillActiveTurn =
                            deriveNewestTurn(state.messages[0], state.messages[1]).assistantMessageId === assistantMessageId;
                        if (isStillActiveTurn && state.isLoading) {
                            abortStreamRef.current?.();
                            abortStreamRef.current = null;
                            dispatch(setIsLoading(false));
                        }
                        dispatch(updateMessage(assistantMessageId, '', {}, MESSAGE_STATUS.PARTIAL));
                    }, STOP_SETTLE_FALLBACK_MS);
                }
                return true;
            })
            .catch(() => false); // already logged in stopAskAicaRun
    }, [
        newestTurn.userMessageId,
        newestTurn.assistantMessageId,
        org_id,
        user_id,
        dispatch,
        pendingClarification,
        patchClarificationCard,
        mergeHistoryIntoThread,
        selectedConversationId,
    ]);

    return {
        messages,
        reasoning,
        isLoading,
        error,
        sendMessage,
        clearChatMemory,
        newChat,
        deleteConversation,
        pendingNewConversation,
        registerStreamStartedCallback,
        isLoadingChatHistory,
        messagesEndRef,
        fetchChatHistory,
        fetchLibraryFiles,
        fetchOrgConversations,
        hasMoreData: pagination.hasMoreData,
        hasInProgressTurn: newestTurn.isInProgress,
        pendingClarification,
        hasPendingClarification: !!pendingClarification,
        answerClarification,
        stopRun,
        reattachExhausted,
    };
};

export default useAskAica;
